/**
 * Promo codes for an organization.
 *
 * Replaces a stub that hardcoded "No promo codes created yet" and whose Add buttons navigated to
 * `/promo-codes/new` — a route that does not exist in router.tsx, so it was a dead button in
 * production.
 *
 * WHY SELECTING A DEFAULT WRITES organizations.promo_code
 * Existing CTAs resolve `organizations.promo_code`, and those CTAs are deliberately untouched. A
 * default flag that lived only in `promo_codes` would therefore be inert — nothing reads it.
 * `set_organization_default_promo_code` moves the flag and that scalar together in one
 * transaction, so the organization's choice takes effect without the CTA mechanism changing at all,
 * and the two can never disagree.
 *
 * AUTHORIZATION
 * The mutating controls mirror what set_organization_default_promo_code actually enforces:
 * role IN ('superadmin','admin') OR a user_organization_access grant on THIS organization.
 * Gating on role alone would hide the control from org users the server would have permitted;
 * showing it to everyone (the previous behaviour) offered buttons that always failed server-side.
 * The server check remains authoritative — this only stops us advertising an action that is refused.
 */
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PlusIcon,
  TagIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { supabase } from "../../services/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { normalizeRole } from "../../utils/roleUtils";
import Card from "../ui/Card";
import Button from "../common/Button";

interface PromoCode {
  id: string;
  organization_id: string;
  code: string;
  description: string | null;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
}

interface Props {
  organizationId: string;
}

const OrganizationPromoCodesTab: React.FC<Props> = ({ organizationId }) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isPlatformAdmin = ["admin", "superadmin"].includes(
    normalizeRole(user?.role),
  );

  // Second half of the server predicate: an explicit grant on THIS organization.
  const { data: hasOrgGrant = false } = useQuery({
    queryKey: ["organization-access", organizationId, user?.id],
    enabled: !!user?.id && !isPlatformAdmin,
    queryFn: async (): Promise<boolean> => {
      const { data, error: err } = await supabase
        .from("user_organization_access")
        .select("user_id")
        .eq("organization_id", organizationId)
        .eq("user_id", user!.id)
        .limit(1);
      if (err) {
        throw err;
      }
      return (data ?? []).length > 0;
    },
  });

  const canManage = isPlatformAdmin || hasOrgGrant;
  const [newCode, setNewCode] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data: codes = [], isLoading } = useQuery({
    queryKey: ["organization-promo-codes", organizationId],
    queryFn: async (): Promise<PromoCode[]> => {
      const { data, error: err } = await supabase
        .from("promo_codes")
        .select(
          "id, organization_id, code, description, is_default, is_active, created_at",
        )
        .eq("organization_id", organizationId)
        .order("is_default", { ascending: false })
        .order("created_at", { ascending: true });
      if (err) {
        throw err;
      }
      return (data ?? []) as PromoCode[];
    },
    enabled: !!organizationId,
  });

  const addMutation = useMutation({
    mutationFn: async () => {
      const code = newCode.trim();
      if (!code) {
        throw new Error("Enter a promo code.");
      }
      // First code for an organization becomes its default, so an org is never left with codes
      // but no selection — the state migration -015 also refuses.
      const isFirst = codes.length === 0;
      const { error: err } = await supabase.from("promo_codes").insert({
        organization_id: organizationId,
        code,
        description: newDescription.trim() || null,
        is_default: isFirst,
        is_active: true,
      });
      if (err) {
        throw err;
      }
      return code;
    },
    onSuccess: () => {
      setNewCode("");
      setNewDescription("");
      setError(null);
      queryClient.invalidateQueries({
        queryKey: ["organization-promo-codes", organizationId],
      });
      queryClient.invalidateQueries({
        queryKey: ["organization", organizationId],
      });
    },
    onError: (e: unknown) => {
      // The table carries UNIQUE(code) globally, so a code already used by another organization is
      // refused. Say that plainly rather than surfacing a constraint name.
      const msg = e instanceof Error ? e.message : String(e);
      setError(
        msg.includes("duplicate key") || msg.includes("promo_codes_code_key")
          ? "That code is already in use by another organization."
          : msg,
      );
    },
  });

  const selectMutation = useMutation({
    mutationFn: async (promoCodeId: string) => {
      const { error: err } = await supabase.rpc(
        "set_organization_default_promo_code",
        { p_organization_id: organizationId, p_promo_code_id: promoCodeId },
      );
      if (err) {
        throw err;
      }
    },
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({
        queryKey: ["organization-promo-codes", organizationId],
      });
      queryClient.invalidateQueries({
        queryKey: ["organization", organizationId],
      });
    },
    onError: (e: unknown) =>
      setError(e instanceof Error ? e.message : String(e)),
  });

  return (
    <Card>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          Promotional Codes
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          An organization can hold more than one promo code. Any code listed
          here can be chosen on this organization's CTAs. The selected code is
          the one shown on the organization record and used for reporting, and
          it is offered first when building a CTA.
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {!canManage && (
        <p className="mb-4 text-sm text-gray-500">
          You can view this organization&apos;s promo codes. Changing them
          requires administrator access.
        </p>
      )}

      {/* Add */}
      {canManage && (
        <div className="mb-6 flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-700">
              Promo code
            </label>
            <input
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              placeholder="e.g., SUMMER2025"
              className="mt-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div className="flex-1 min-w-[12rem]">
            <label className="block text-xs font-medium text-gray-700">
              Description (optional)
            </label>
            <input
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="What this code is for"
              className="mt-1 w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <Button
            onClick={() => addMutation.mutate()}
            disabled={!newCode.trim() || addMutation.isPending}
          >
            <PlusIcon className="h-5 w-5 mr-1" />
            {addMutation.isPending ? "Adding..." : "Add"}
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="py-8 text-center text-sm text-gray-500">
          Loading promo codes...
        </div>
      ) : codes.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <TagIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No promo codes created yet.
          </h3>
        </div>
      ) : (
        <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg">
          {codes.map((pc) => (
            <li key={pc.id} className="flex items-center gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-gray-900">
                    {pc.code}
                  </span>
                  {pc.is_default && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                      <CheckCircleIcon className="h-3.5 w-3.5" />
                      Selected
                    </span>
                  )}
                  {!pc.is_active && (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      Inactive
                    </span>
                  )}
                </div>
                {pc.description && (
                  <p className="mt-0.5 truncate text-xs text-gray-500">
                    {pc.description}
                  </p>
                )}
              </div>
              {/* Selection only appears once there is a choice to make — a single code is already
                  the selected one and a button would suggest otherwise. */}
              {canManage && codes.length > 1 && !pc.is_default && (
                <button
                  onClick={() => selectMutation.mutate(pc.id)}
                  disabled={selectMutation.isPending}
                  className="px-3 py-1.5 text-sm font-medium text-primary-700 border border-primary-300 rounded-lg hover:bg-primary-50 disabled:opacity-50"
                >
                  {selectMutation.isPending ? "Selecting..." : "Use this code"}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

export default OrganizationPromoCodesTab;
